import { useState } from 'react';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Mail, User, MessageSquare, Send, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

// EmailJS Konfiguration
const EMAILJS_SERVICE_ID = 'service_4pwjpg8';
const EMAILJS_TEMPLATE_ID = 'template_ypbgwbg';
const EMAILJS_PUBLIC_KEY = '427h8UG6FX_IFv-XW';

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // E-Mail über EmailJS senden
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          from_name: formData.name,
          from_email: formData.email,
          subject: formData.subject,
          message: formData.message,
        },
        EMAILJS_PUBLIC_KEY
      );
      
      setSuccess(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
      toast.success('Nachricht erfolgreich gesendet!');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Fehler beim Senden. Bitte versuche es erneut.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md"
        >
          <div className="w-20 h-20 bg-sage/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-sage" />
          </div>
          <h2 className="font-display text-2xl font-bold text-navy mb-4">
            Vielen Dank!
          </h2>
          <p className="text-olive mb-6">
            Deine Nachricht wurde erfolgreich gesendet. Wir melden uns so schnell wie möglich bei dir.
          </p>
          <Button 
            onClick={() => setSuccess(false)}
            className="bg-terracotta hover:bg-terracotta/90 text-white rounded-full px-6"
          >
            Weitere Nachricht senden
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Header */}
          <div className="text-center mb-10">
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-navy mb-4">
              Kontakt
            </h1>
            <p className="text-olive text-lg">
              Hast du Fragen, Anregungen oder Feedback? Schreib uns!
            </p>
          </div>

          {/* Form */}
          <div className="bg-white rounded-3xl shadow-sm p-6 sm:p-10">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name" className="text-navy">Name</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-olive" />
                    <Input
                      id="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Dein Name"
                      className="pl-10 py-5 rounded-xl"
                      required
                      data-testid="contact-name"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email" className="text-navy">E-Mail</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-olive" />
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="deine@email.de"
                      className="pl-10 py-5 rounded-xl"
                      required
                      data-testid="contact-email"
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="subject" className="text-navy">Betreff</Label>
                <div className="relative mt-1">
                  <MessageSquare className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-olive" />
                  <Input
                    id="subject"
                    type="text"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    placeholder="Worum geht es?"
                    className="pl-10 py-5 rounded-xl"
                    required
                    data-testid="contact-subject"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="message" className="text-navy">Nachricht</Label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Deine Nachricht..."
                  className="mt-1 rounded-xl min-h-[150px]"
                  required
                  data-testid="contact-message"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-terracotta hover:bg-terracotta/90 text-white rounded-xl py-6 font-semibold gap-2"
                disabled={loading}
                data-testid="contact-submit"
              >
                <Send className="w-5 h-5" />
                {loading ? 'Wird gesendet...' : 'Nachricht senden'}
              </Button>
            </form>
          </div>

          {/* Additional Info */}
          <div className="mt-8 text-center">
            <p className="text-olive">
              Oder schreib uns direkt an{' '}
              <a href="mailto:info@dryfroggo.de" className="text-terracotta font-medium hover:underline">
                info@dryfroggo.de
              </a>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};
